import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Check, X, Link2 } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { azError } from "@/lib/az-errors";
import {
  PAYMENT_STATUS_LABEL,
  listAllPaymentRequests,
  updatePaymentRequest,
} from "@/lib/payments";

export const Route = createFileRoute("/_authenticated/admin/odenis-sorgulari")({
  component: Page,
});

function Page() {
  const qc = useQueryClient();
  const [links, setLinks] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["admin-payment-requests"],
    queryFn: listAllPaymentRequests,
  });

  const update = useMutation({
    mutationFn: (v: { id: string; status?: string; payment_link?: string | null }) =>
      updatePaymentRequest(v.id, {
        ...(v.status ? { status: v.status } : {}),
        ...(v.payment_link !== undefined ? { payment_link: v.payment_link } : {}),
      }),
    onSuccess: () => {
      toast.success("Yeniləndi");
      void qc.invalidateQueries({ queryKey: ["admin-payment-requests"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  const rows = data ?? [];
  const pending = rows.filter((r) => r.status === "pending").length;
  const paidSum = rows
    .filter((r) => r.status === "paid")
    .reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div>
      <AdminPageHeader
        title="Ödəniş sorğuları"
        description="İstifadəçilərin ödəniş müraciətləri və ödəniş linkləri"
      />

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Ümumi sorğu", value: rows.length },
          { label: "Gözləyən", value: pending },
          { label: "Ödənilib (AZN)", value: paidSum.toFixed(2) },
        ].map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
              <CreditCard className="size-4" />
              {c.label}
            </div>
            <p className="mt-2 text-xl font-extrabold">{isLoading ? "…" : c.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-2">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Yüklənir…</p>
        ) : rows.length === 0 ? (
          <p className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
            Hələ ödəniş sorğusu yoxdur.
          </p>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border bg-card p-3 shadow-card">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold">
                    {Number(r.amount).toFixed(2)} AZN ·{" "}
                    <span className="text-xs font-semibold text-muted-foreground">
                      {PAYMENT_STATUS_LABEL[r.status] ?? r.status}
                    </span>
                  </p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    İstifadəçi: {r.user_id.slice(0, 8)}… ·{" "}
                    {r.listing_id ? `Elan: ${r.listing_id.slice(0, 8)}…` : "Elan yoxdur"} ·{" "}
                    {new Date(r.created_at).toLocaleString("az-AZ", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </p>
                  {r.note && <p className="mt-1 text-xs">{r.note}</p>}
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => update.mutate({ id: r.id, status: "paid" })}
                    className="inline-flex items-center gap-1 rounded-xl border border-emerald-500/40 px-3 py-1.5 text-xs font-semibold text-emerald-600 hover:bg-emerald-500/10"
                  >
                    <Check className="size-3.5" /> Ödənildi
                  </button>
                  <button
                    type="button"
                    onClick={() => update.mutate({ id: r.id, status: "rejected" })}
                    className="inline-flex items-center gap-1 rounded-xl border border-destructive/40 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10"
                  >
                    <X className="size-3.5" /> Rədd et
                  </button>
                </div>
              </div>

              <div className="mt-2 flex items-center gap-2">
                <Link2 className="size-4 shrink-0 text-muted-foreground" />
                <input
                  value={links[r.id] ?? r.payment_link ?? ""}
                  onChange={(e) => setLinks((p) => ({ ...p, [r.id]: e.target.value }))}
                  placeholder="Ödəniş linki (məs. m10, kapital, bank linki)"
                  className="h-9 flex-1 rounded-xl border border-border bg-background px-3 text-xs"
                />
                <button
                  type="button"
                  onClick={() =>
                    update.mutate({ id: r.id, payment_link: links[r.id] ?? r.payment_link ?? "" })
                  }
                  className="h-9 rounded-xl bg-primary px-3 text-xs font-bold text-primary-foreground"
                >
                  Yadda saxla
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
